import React from 'react';
import { GetPlatePadding, GetUIRect } from 'utils/Global';
import { RMRectangle, WellRectangle } from 'business/models/CustomModels';
import { Coordinates, NumberRange } from 'constants/';
import Konva from 'konva';
import { Rectangle } from './constants';

const defaultPlatePosition = {
  x: 0,
  y: 0,
};

const drawCellRectangles = (rows: number, cols: number, wells: WellRectangle[]) => {
  const cells: React.ReactNode[] = [];

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const wellRect: RMRectangle = wells[r * cols + c];
      const rect: RMRectangle = {
        x: wellRect.x,
        y: wellRect.y,
        w: wellRect.w,
        h: wellRect.h,
        bColor: 'black',
      };
      rect.listening = false;
      rect.opacity = 1;
      rect.fillColor = '';
      cells.push(GetUIRect(rect));
    }
  }

  return cells;
};

const getWellFromAbsPoint = (
  containerRows: number,
  containerCols: number,
  point: Coordinates,
  wellRects: RMRectangle[],
  canvasRectangleSize: number,
  selectionMargin: NumberRange<1, 100> = null,
): Coordinates => {
  const padding = GetPlatePadding() / 2;

  let wellNumXFromStart = (point.x - padding) / canvasRectangleSize;
  wellNumXFromStart = wellNumXFromStart >= 0 ? wellNumXFromStart : 0;
  wellNumXFromStart = Math.floor(wellNumXFromStart);

  let wellNumYFromStart = (point.y - padding) / canvasRectangleSize;
  wellNumYFromStart = wellNumYFromStart >= 0 ? wellNumYFromStart : 0;
  wellNumYFromStart = Math.floor(wellNumYFromStart);

  const wellNum = wellNumXFromStart + wellNumYFromStart * containerCols;
  let wellRect = wellRects[wellNum];
  if (!wellRect) {
    wellRect = wellRects[wellRects.length - 1];
  }
  if (selectionMargin) {
    const isInXRange =
      point.x - wellRect.x < wellRect.w * (selectionMargin / 100) ||
      point.x > wellRect.x + wellRect.w;
    const isInYRange =
      point.y - wellRect.y < wellRect.h * (selectionMargin / 100) ||
      point.y > wellRect.y + wellRect.h;

    if (isInXRange && wellNumXFromStart > 0) {
      wellNumXFromStart--;
    }

    if (isInYRange && wellNumYFromStart > 0) {
      wellNumYFromStart--;
    }
  }
  return { x: wellNumXFromStart, y: wellNumYFromStart };
};

const getWellNumbersBetweenStartAndEnd = (
  startWellLoc: Coordinates,
  endWellLoc: Coordinates,
  containerRows: number,
  containerCols: number,
) => {
  if (startWellLoc.x > endWellLoc.x) {
    [startWellLoc.x, endWellLoc.x] = [endWellLoc.x, startWellLoc.x];
  }

  if (startWellLoc.y > endWellLoc.y) {
    [startWellLoc.y, endWellLoc.y] = [endWellLoc.y, startWellLoc.y];
  }

  const wellsInRange: number[] = [];

  for (let y = startWellLoc.y; y <= endWellLoc.y && y < containerRows; y++) {
    for (let x = startWellLoc.x; x <= endWellLoc.x && x < containerCols; x++) {
      wellsInRange.push(y * containerCols + x + 1);
    }
  }

  return wellsInRange;
};

/**
 * Utility to zoom and center the canvas to a selected rectangle.
 * @param stage - Konva Stage instance
 * @param rect - Rectangle area to zoom into
 * @param duration - Animation duration (optional)
 */
function zoomToFocusArea(
  stage: Konva.Stage,
  rect: Rectangle,
  offset: number = 0,
  duration: number = 0,
  onComplete?: (payload: {
    oldScale: number;
    newScale: number;
    newPosition: { x: number; y: number };
  }) => void,
) {
  if (!stage || !rect) {
    return;
  }

  const canvasWidth = stage.width();
  const canvasHeight = stage.height();
  const oldScale = stage.scaleX() || 1;

  const scaleX = canvasWidth / rect.width;
  const scaleY = canvasHeight / rect.height;
  const newScale = Math.min(scaleX, scaleY);

  const newPosX = -rect.x * newScale;
  const newPosY = -rect.y * newScale;

  const newPosition = {
    x: newPosX + offset,
    y: newPosY,
  };

  stage.to({
    scaleX: newScale,
    scaleY: newScale,
    x: newPosition.x,
    y: newPosition.y,
    duration,
    onFinish: () => {
      onComplete?.({ oldScale, newScale, newPosition });
    },
  });
}

function platePosition(
  stage: Konva.Stage,
  rect: Rectangle,
  onComplete?: (payload: { newPosition: { x: number; y: number } }) => void,
) {
  if (!stage || !rect) {
    return;
  }
  const referencePoint = {
    x: 0.5 * rect.width + (rect.width - defaultPlatePosition.x),
    y: 0.5 * rect.height + (rect.height - defaultPlatePosition.y),
  };

  const newPosition = {
    x: rect.width - referencePoint.x + 0.5 * rect.width,
    y: rect.height - referencePoint.y + 0.5 * rect.height,
  };
  stage.to({
    x: newPosition.x,
    y: newPosition.y,
    onFinish: () => {
      onComplete?.({ newPosition });
    },
  });
}

export {
  drawCellRectangles,
  getWellFromAbsPoint,
  getWellNumbersBetweenStartAndEnd,
  zoomToFocusArea,
  platePosition,
};
